import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEmployees } from '../contexts/EmployeeContext';
import { useStore } from '../contexts/StoreContext';
import { LogOut, Calendar, Users, Home, BarChart3, FileText, CalendarDays, Building2, UserX, User, Settings } from 'lucide-react';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useCompactMode } from '../contexts/CompactModeContext';
import { EmployeeManagement } from './EmployeeManagement';
import ScheduleManagement from './ScheduleManagement';
import { StoreSettings } from './StoreSettings';
import { Statistics } from './Statistics';
import { ExportTools } from './ExportTools';
import { AbsenceManagement } from './AbsenceManagement';
import { HolidayIntegration } from './HolidayIntegration';
import { ThemeToggle } from './ThemeToggle';
import { BirthdayNotification } from './BirthdayNotification';
import { NotificationCenter } from './NotificationCenter';
import { KeyboardShortcuts } from './KeyboardShortcuts';
import { Logo } from './Logo';
import { HamburgerMenu } from './HamburgerMenu';
import { UserMenu } from './UserMenu';
import { UserAvatar } from './UserAvatar';
import { CompanySettings } from './CompanySettings';
import { PermissionsManagement } from './PermissionsManagement';
import { HistoryManagement } from './HistoryManagement';
import { TimeClockHistory } from './TimeClockHistory';
import { useLanguage } from '../contexts/LanguageContext';
import { isIT } from '../utils/rolePermissions';
import { Shield, History, Clock } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

type TabType = 'schedule' | 'employees' | 'settings' | 'statistics' | 'export' | 'absences' | 'holidays' | 'company' | 'permissions' | 'history' | 'timeClockHistory';

export function ManagerDashboard() {
  const { currentEmployee, logout, isDistrictManager } = useAuth();
  const { employees } = useEmployees();
  const { currentStore, setCurrentStore } = useStore();
  const { isCompactMode, toggleCompactMode, isMobile } = useCompactMode();
  const { t } = useLanguage();
  const permissions = usePermissions();
  // Si es IT, iniciar en Configuración Empresa, sino en schedule
  const [activeTab, setActiveTab] = useState<TabType>(
    currentEmployee && isIT(currentEmployee.role) ? 'company' : 'schedule'
  );
  const [showBirthdayNotification, setShowBirthdayNotification] = useState(true);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Si es IT, siempre mostrar Configuración Empresa, Permisos e Historial (si tiene permiso)
  const isITUser = currentEmployee ? isIT(currentEmployee.role) : false;
  const tabs = isITUser
    ? [
        { id: 'company' as TabType, label: 'Configuración Empresa', icon: Settings },
        { id: 'permissions' as TabType, label: 'Permisos', icon: Shield },
        ...(permissions.history?.read ? [{ id: 'history' as TabType, label: 'Historial', icon: History }] : []),
        ...(permissions.timeClockHistory?.read ? [{ id: 'timeClockHistory' as TabType, label: 'Historial de Fichadas', icon: Clock }] : [])
      ]
    : [
        ...(permissions.schedule?.read ? [{ id: 'schedule' as TabType, label: t('schedule'), icon: Calendar }] : []),
        ...(permissions.employees?.read ? [{ id: 'employees' as TabType, label: t('employees'), icon: Users }] : []),
        ...(permissions.absences?.read ? [{ id: 'absences' as TabType, label: t('vacations'), icon: UserX }] : []),
        ...(permissions.storeSchedule?.read ? [{ id: 'settings' as TabType, label: 'Horarios Tienda', icon: Home }] : []),
        ...(permissions.statistics?.read ? [{ id: 'statistics' as TabType, label: t('statistics'), icon: BarChart3 }] : []),
        ...(permissions.export?.read ? [{ id: 'export' as TabType, label: t('export'), icon: FileText }] : []),
        ...(permissions.history?.read ? [{ id: 'history' as TabType, label: 'Historial', icon: History }] : []),
        ...(permissions.timeClockHistory?.read ? [{ id: 'timeClockHistory' as TabType, label: 'Historial de Fichadas', icon: Clock }] : [])
      ];

  // Función para volver al selector de tiendas (solo para encargados de distrito)
  const handleBackToStoreSelector = () => {
    // Limpiar la tienda seleccionada para volver al selector
    localStorage.removeItem('horarios_current_store_id');
    // Limpiar el estado de la tienda actual - esto hará que AppRouter muestre el selector
    setCurrentStore('');
  };

  // Atajos de teclado
  useKeyboardShortcuts([
    {
      key: '/',
      ctrlKey: true,
      action: () => setShowKeyboardHelp(!showKeyboardHelp),
      description: 'Mostrar/ocultar ayuda de atajos'
    },
    {
      key: '1',
      ctrlKey: true,
      action: () => setActiveTab('schedule'),
      description: 'Ir a pestaña Horarios'
    },
    {
      key: '2',
      ctrlKey: true,
      action: () => setActiveTab('employees'),
      description: 'Ir a pestaña Empleados'
    },
    {
      key: '3',
      ctrlKey: true,
      action: () => setActiveTab('absences'),
      description: 'Ir a pestaña Vacaciones y Ausencias'
    },
    {
      key: '4',
      ctrlKey: true,
      action: () => setActiveTab('holidays'),
      description: 'Ir a pestaña Feriados'
    },
    {
      key: '5',
      ctrlKey: true,
      action: () => setActiveTab('settings'),
      description: 'Ir a pestaña Tienda'
    },
    {
      key: '6',
      ctrlKey: true,
      action: () => setActiveTab('statistics'),
      description: 'Ir a pestaña Estadísticas'
    },
    {
      key: '7',
      ctrlKey: true,
      action: () => setActiveTab('export'),
      description: 'Ir a pestaña Exportar'
    }
  ]);

  const renderContent = () => {
    switch (activeTab) {
      case 'schedule':
        return <ScheduleManagement />;
      case 'employees':
        return <EmployeeManagement />;
      case 'absences':
        return <AbsenceManagement />;
      case 'holidays':
        return <HolidayIntegration />;
      case 'settings':
        return <StoreSettings />;
      case 'statistics':
        return <Statistics />;
      case 'export':
        return <ExportTools />;
      case 'company':
        return <CompanySettings />;
      case 'permissions':
        return <PermissionsManagement />;
      case 'history':
        return <HistoryManagement />;
      case 'timeClockHistory':
        return <TimeClockHistory />;
      default:
        // Si es IT, mostrar Configuración Empresa por defecto
        if (currentEmployee && isIT(currentEmployee.role)) {
          return <CompanySettings />;
        }
        return <ScheduleManagement />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* Solo mostrar logo y título en desktop */}
              {!isMobile ? (
                <>
                  <Logo />
                  <div className="ml-4">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {t('scheduleAdministrationPanel')}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('welcome')}, {currentEmployee?.name}
                      {currentStore && !(currentEmployee && isIT(currentEmployee.role) && !currentEmployee.storeId) && (
                        <span className="ml-2 text-blue-600 dark:text-blue-400">
                          • {currentStore.name}
                        </span>
                      )}
                    </p>
                  </div>
                </>
              ) : (
                /* En móvil solo mostrar saludo */
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('welcome')}, {currentEmployee?.name}
                  </h1>
                  {currentStore && !(currentEmployee && isIT(currentEmployee.role) && !currentEmployee.storeId) && (
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      {currentStore.name}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <NotificationCenter 
                employees={employees}
                currentEmployee={currentEmployee}
                isManager={true}
              />
              
              {/* Solo mostrar en desktop */}
              {!isMobile && (
                <>
                  <KeyboardShortcuts 
                    currentTab={activeTab}
                    isManager={true}
                  />
                  <button
                    onClick={() => setShowUserMenu(true)}
                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors dark:text-gray-400 dark:hover:text-gray-100"
                    title="Usuario"
                  >
                    <UserAvatar size="sm" />
                  </button>
                  <ThemeToggle />
                </>
              )}
              
              {/* Botón de volver al selector de tiendas - según permiso stores */}
              {permissions.stores?.read && !isMobile && (
                <button
                  onClick={handleBackToStoreSelector}
                  className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20"
                  title={t('back')}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  {t('store')}
                </button>
              )}
              
              {/* Botón de cerrar sesión - solo visible en desktop */}
              {!isMobile && (
                <button
                  onClick={logout}
                  className="flex items-center text-gray-600 hover:text-gray-900 transition-colors dark:text-gray-400 dark:hover:text-gray-100"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  {t('logout')}
                </button>
              )}
              
            </div>
            
            {/* Menú hamburguesa - solo visible en móvil */}
            <HamburgerMenu 
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab as TabType);
              }}
              isManager={true}
              onShowKeyboardHelp={() => setShowKeyboardHelp(true)}
              onLogout={logout}
              onBackToStoreSelector={permissions.stores?.read ? handleBackToStoreSelector : undefined}
              onShowUserMenu={() => setShowUserMenu(true)}
            />
          </div>
        </div>
      </header>

      {/* Navigation Tabs - solo visible en desktop */}
      {!isMobile && (
        <nav className="bg-white border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </main>

      {/* Birthday Notification */}
      {showBirthdayNotification && (
        <BirthdayNotification 
          employees={employees}
          onClose={() => setShowBirthdayNotification(false)}
        />
      )}

      {/* User Menu */}
      <UserMenu 
        isOpen={showUserMenu}
        onClose={() => setShowUserMenu(false)}
      />

      {/* Powered by Tempo */}
      <footer className="mt-12 py-6 border-t border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>Powered by</span>
            <img 
              src="/images/tempo.png" 
              alt="Tempo" 
              className="h-6 w-auto"
            />
          </div>
        </div>
      </footer>

    </div>
  );
}


